import { useId, useState } from 'react';
import { UserX } from 'lucide-react';
import { useT } from '../../i18n';
import { closeMyAccount } from '../../lib/api/accountClosure';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// «AVSLUTT KONTOEN» — THE PERSON CLOSES THEIR OWN ACCOUNT (#1062).
//
// Account closure, the first of the three data-subject operations: the account
// stops signing in and its sessions and passkeys are revoked. Nothing else is
// deleted, and the screen says so — closure is not erasure, and somebody who
// reads «avslutt» as «delete everything» must not be misled either way.
//
// TWO STEPS AND NO `window.confirm()` (DOMAIN_RULES): a button that arms, then
// a named confirmation beside a cancel. The server wants a sign-in from the
// last fifteen minutes; an older one is answered `reauthentication_required`,
// the transport opens the step-up prompt, and the person presses again.
// ═══════════════════════════════════════════════════════════════════

export interface CloseAccountSectionProps {
  /**
   * Leave the signed-in surface once the account is closed — the app's own
   * sign-out path. The server has already revoked the session; this tidies the
   * client. Absent where the host has none, and the done state stands alone.
   */
  onClosed?: (() => void) | null;
  /** Injectable for tests. */
  close?: typeof closeMyAccount;
}

type State =
  | { kind: 'idle' }
  | { kind: 'armed' }
  | { kind: 'working' }
  | { kind: 'done' }
  | { kind: 'failed'; code: string };

export function CloseAccountSection({
  onClosed = null,
  close = closeMyAccount,
}: CloseAccountSectionProps) {
  const t = useT();
  const uid = safeId('close-account', useId());
  const titleId = `${uid}-title`;
  const [state, setState] = useState<State>({ kind: 'idle' });

  const failure = (code: string): string => {
    if (code === 'reauthentication_required') return t('account.close.reauth');
    if (code === 'last_role_manager') return t('account.close.lastRoleManager');
    if (code === 'unreachable') return t('account.close.unreachable');
    return t('account.close.failed');
  };

  const confirm = async () => {
    setState({ kind: 'working' });
    const res = await close();
    setState(res.code === null ? { kind: 'done' } : { kind: 'failed', code: res.code });
  };

  return (
    <section className="card p-5" aria-labelledby={titleId} data-testid="close-account">
      <h2
        id={titleId}
        className="font-display text-lg font-bold text-ink inline-flex items-center gap-2"
      >
        <UserX size={18} aria-hidden="true" /> {t('account.close.title')}
      </h2>

      {state.kind === 'done' ? (
        <div className="mt-2 flex flex-col gap-3">
          <p className="text-sm text-ink" role="status">
            {t('account.close.done')}
          </p>
          {onClosed && (
            <div>
              <button type="button" className="btn-outline" onClick={onClosed}>
                {t('account.close.continue')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <p className="mt-1 text-sm text-ink-2">{t('account.close.lead')}</p>
          <p className="mt-1 text-xs text-ink-3">{t('account.close.kept')}</p>

          {state.kind === 'idle' || state.kind === 'failed' ? (
            <div className="mt-3">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setState({ kind: 'armed' })}
                data-testid="close-account-start"
              >
                {t('account.close.start')}
              </button>
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              <p className="text-sm font-medium text-ink">{t('account.close.confirmLead')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-danger"
                  disabled={state.kind === 'working'}
                  onClick={() => void confirm()}
                  data-testid="close-account-confirm"
                >
                  {state.kind === 'working'
                    ? t('account.close.working')
                    : t('account.close.confirm')}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={state.kind === 'working'}
                  onClick={() => setState({ kind: 'idle' })}
                  data-testid="close-account-cancel"
                >
                  {t('account.close.cancel')}
                </button>
              </div>
            </div>
          )}

          {state.kind === 'failed' && (
            <p className="mt-2 text-sm text-rose-700" role="alert">
              {failure(state.code)}
            </p>
          )}
        </>
      )}
    </section>
  );
}
